import {
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

interface SegmentedTabOption<TValue extends string> {
  value: TValue;
  /** Usually plain text; can be an icon for icon-only tabs. When not plain
   *  text, pass `ariaLabel` so the tab still has an accessible name. */
  label: ReactNode;
  /** Accessible name override — required when `label` isn't plain text (e.g. an icon). */
  ariaLabel?: string;
  /** Optional badge text rendered as a pill after the label (e.g. "Coming soon"). */
  badge?: string;
  /** `'problem'` renders the badge in error tone instead of the default primary — G1: colour
   *  only for problems (e.g. a batch's Images tab badging real failures, not just a count).
   *  `'attention'` is the amber "waiting on a person" chip (work for the user, not a fault). */
  badgeTone?: 'default' | 'problem' | 'attention' | 'muted';
  /** When true the tab is non-interactive and renders with cursor-not-allowed. */
  disabled?: boolean;
  /** Optional hover tooltip — e.g. explaining why a disabled tab is unavailable.
   *  The tab is `aria-disabled` (not natively `disabled`), so it still receives
   *  the pointer events the tooltip needs. */
  tooltip?: ReactNode;
  /** Stable id for the tab button — paired with the panel's `aria-labelledby`
   *  so SR users hear the tab name when focus enters the panel. */
  tabId?: string;
  /** A small status dot after the label instead of a badge (e.g. a tab waiting on the user);
   *  `dotLabel` is read to screen readers. */
  dot?: 'attention' | 'problem';
  dotLabel?: string;
  /** Makes the tab a real link (`<a href>`): cmd/ctrl/middle-click opens it in a new tab and the
   *  URL can be copied; a plain click still goes through `onChange` (the caller navigates). */
  href?: string;
}

/** A click the browser should handle itself (new tab / window / download), not the SPA. */
function isModifiedClick(e: MouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}

/** Exported so a caller can wrap this control without re-declaring its props. */
export interface SegmentedTabsProps<TValue extends string> {
  value: TValue;
  onChange: (value: TValue) => void;
  options: SegmentedTabOption<TValue>[];
  /** a11y label for the tablist (e.g. "Treatment configuration sections"). */
  ariaLabel: string;
  /** Optional id of the controlled `<div role="tabpanel">` — passed via
   *  `aria-controls` on every tab so AT can navigate between the tab and
   *  its panel. */
  panelId?: string;
  className?: string;
  /** 'default' (px-5 py-2, sized for text labels), 'sm' (32px tabs in a 40px box, to sit
   *  level with buttons and inputs in a toolbar), 'xs' (28px box, 12.5px label — a dense
   *  toolbar sitting among compact row controls) or 'icon' (p-2, sized for icon-only tabs).
   *  On `variant="underline"`, 'sm'/'xs' shrink the tab height/label from the 44px/15px
   *  default to a dense 36px/13.5px page-level strip; 'icon' is unused there. Defaults to
   *  'default'. */
  size?: 'default' | 'sm' | 'xs' | 'icon';
  /** 'segmented' (bordered box, sliding filled pill — the default), 'underline'
   *  (bottom hairline, primary underline under the active tab — page-level tabs), 'pill'
   *  (no box; a soft primary pill slides behind the active tab — section nav in a header bar)
   *  or 'tray' (a quiet neutral tray; the active tab is a raised card — status views). */
  variant?: 'segmented' | 'underline' | 'pill' | 'tray';
  /** Render the bar even with a single option. Off by default: one tab is no choice,
   *  so the bar is hidden; set it only where the lone tab still carries meaning. */
  showSingle?: boolean;
  /** Automation hook: sets `data-testid` on the tablist and `${testId}-${value}` on each tab. */
  testId?: string;
}

/**
 * Sliding segmented tab control. An absolutely-positioned pill translates
 * behind the active tab so switching feels like a physical slide rather than
 * an instant swap. Matches the Configuration Manager and Treatment Configuration
 * toggles.
 *
 * Accessibility: WAI-ARIA tab control — `role="tablist"` on the container,
 * `role="tab"` + `aria-selected` on each button, and (when `panelId` is
 * provided) `aria-controls` linking to the panel. Consumer renders the panel
 * with `role="tabpanel"` and `aria-labelledby` pointing at the active tab id.
 *
 * Keyboard (WAI-ARIA tabs, automatic activation): Left/Up and Right/Down move
 * to the previous/next enabled tab (wrapping), Home/End jump to the first/last;
 * moving selects and focuses that tab, so the inactive segment is reachable
 * without a pointer. Roving tabindex keeps only the active tab in the Tab order.
 *
 * `variant="underline"` swaps the box and filled pill for a bottom hairline and a
 * 2px primary underline (the same measured element) — for page-level tab strips.
 *
 * With one option or none it renders nothing (a single tab is no choice) unless
 * `showSingle` is set.
 *
 * This is a non-routing variant: state lives in the parent (`useState`).
 */
export function SegmentedTabs<TValue extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  panelId,
  className,
  size = 'default',
  variant = 'segmented',
  showSingle = false,
  testId,
}: SegmentedTabsProps<TValue>) {
  const underline = variant === 'underline';
  const pillNav = variant === 'pill';
  const tray = variant === 'tray';
  const activeIndex = options.findIndex((o) => o.value === value);
  const tabRefs = useRef<(HTMLElement | null)[]>([]);
  const listRef = useRef<HTMLDivElement | null>(null);
  // Measured pill geometry. Tabs are only equal-width when every label is the same
  // length, so the pill tracks the active button's real offset/width instead of
  // assuming an even split — otherwise it overlaps its neighbours.
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null);

  const tabCount = options.length;
  useLayoutEffect(() => {
    const measure = () => {
      const el = tabRefs.current[activeIndex];
      if (!el) return;
      const next = { left: el.offsetLeft, width: el.offsetWidth };
      // Bail out when geometry is unchanged — a fresh object every measure would
      // re-render, and this effect re-runs on render, which would never settle.
      setPill((prev) => (prev?.left === next.left && prev.width === next.width ? prev : next));
    };
    measure();
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return;
    // Re-measure on container/label reflow (responsive width, font swap, i18n change).
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    for (const el of tabRefs.current) if (el) observer.observe(el);
    return () => {
      observer.disconnect();
    };
    // Depends on tabCount, not `options` — callers pass a fresh array literal each render.
  }, [activeIndex, tabCount]);

  /** Select + focus the tab at `index` (skips nothing — caller resolves the target). */
  const activate = (index: number) => {
    const opt = options[index];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    tabRefs.current[index]?.focus();
  };

  /** Nearest enabled index from `start` moving by `dir` (±1), wrapping. */
  const enabledIndex = (start: number, dir: 1 | -1): number => {
    const n = options.length;
    for (let step = 1; step <= n; step++) {
      const i = (start + dir * step + n * step) % n;
      if (!options[i]?.disabled) return i;
    }
    return start;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>, index: number) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        activate(enabledIndex(index, 1));
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        activate(enabledIndex(index, -1));
        break;
      case 'Home':
        e.preventDefault();
        activate(enabledIndex(-1, 1));
        break;
      case 'End':
        e.preventDefault();
        activate(enabledIndex(0, -1));
        break;
    }
  };

  // One option is no choice — render nothing (after the hooks, so their order stays stable).
  if (options.length <= 1 && !showSingle) return null;

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      data-testid={testId}
      className={cn(
        underline
          ? 'border-border relative flex w-max min-w-full border-b'
          : pillNav
            ? 'relative flex w-max items-center gap-1'
            : tray
              ? 'bg-neutral-20 relative flex w-max items-center gap-0.5 rounded-lg p-1'
              : cn(
                  'bg-background border-gray-250 relative flex w-full rounded-lg border-2 sm:max-w-md',
                  size === 'sm'
                    ? 'h-10 items-center p-[3px]'
                    : size === 'xs'
                      ? 'h-7 items-center p-[2px]'
                      : 'p-1',
                ),
        className,
      )}
    >
      {/* Sliding pill — positioned from the active button's measured box so it stays
          aligned even when labels differ in width. Falls back to an even split on the
          first paint, before measurement lands. In the underline variant the same element
          is a 2px bar over the bottom hairline, hidden until measured (tabs aren't equal-width). */}
      <span
        aria-hidden="true"
        className={cn(
          'absolute transition-all duration-200 ease-in-out motion-reduce:transition-none',
          pillNav
            ? 'bg-accent-100 inset-y-0 rounded-full'
            : tray
              ? 'bg-background inset-y-1 rounded-md shadow-sm'
              : 'bg-primary',
          underline
            ? '-bottom-px h-0.5'
            : pillNav || tray
              ? null
              : cn(
                  'rounded-md shadow-sm',
                  size === 'sm'
                    ? 'top-[3px] bottom-[3px]'
                    : size === 'xs'
                      ? 'top-[2px] bottom-[2px]'
                      : 'top-1 bottom-1',
                ),
        )}
        style={
          pill
            ? { left: `${String(pill.left)}px`, width: `${String(pill.width)}px` }
            : underline || pillNav || tray
              ? { left: '0px', width: '0px' }
              : {
                  left: '4px',
                  width: `calc((100% - 8px) / ${String(options.length)})`,
                  transform: `translateX(calc(${String(activeIndex)} * 100%))`,
                }
        }
      />

      {options.map((opt, index) => {
        const active = opt.value === value;
        const Tag = opt.href && !opt.disabled ? 'a' : 'button';
        const button = (
          <Tag
            key={opt.value}
            {...(Tag === 'a' ? { href: opt.href } : { type: 'button' as const })}
            ref={(el: HTMLElement | null) => {
              tabRefs.current[index] = el;
            }}
            {...(opt.tabId ? { id: opt.tabId } : {})}
            role="tab"
            aria-selected={active}
            data-testid={testId ? `${testId}-${opt.value}` : undefined}
            {...(opt.disabled ? { 'aria-disabled': true } : {})}
            tabIndex={active ? 0 : -1}
            {...(opt.ariaLabel ? { 'aria-label': opt.ariaLabel } : {})}
            {...(panelId ? { 'aria-controls': panelId } : {})}
            onClick={(e: MouseEvent) => {
              if (Tag === 'a') {
                if (isModifiedClick(e)) return;
                e.preventDefault();
              }
              if (!opt.disabled) onChange(opt.value);
            }}
            onKeyDown={(e) => {
              handleKeyDown(e, index);
            }}
            className={cn(
              'focus-visible:ring-ring relative z-10 rounded-md text-center whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
              underline
                ? cn(
                    'flex flex-none items-center',
                    size === 'sm' || size === 'xs'
                      ? 'h-9 px-3 text-[13.5px]'
                      : 'h-11 px-3.5 text-[15px]',
                    active ? 'font-semibold' : 'font-medium',
                  )
                : pillNav
                  ? 'flex h-9 flex-none items-center rounded-full px-3.5 text-sm font-medium'
                  : tray
                    ? 'flex h-8 flex-none items-center px-3 text-sm font-medium'
                    : cn(
                        'flex-1 text-sm font-medium',
                        size === 'icon'
                          ? 'flex items-center justify-center p-2'
                          : size === 'sm'
                            ? 'flex h-8 items-center justify-center px-4'
                            : size === 'xs'
                              ? 'flex h-6 items-center justify-center px-2.5 text-[12.5px]'
                              : 'px-5 py-2',
                      ),
              opt.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              // Disabled reads as dimmed with no hover brighten, so it's visibly
              // distinct from a merely-inactive (but selectable) tab. A disabled
              // tab can still be the selected one (deep-linked or forced via URL
              // before its count resolves) — keep its label on the light
              // primary-foreground ramp so it stays legible (muted) on the active
              // pill instead of vanishing into the fill as dark muted-foreground.
              // Underline variant: no fill behind the active tab, so it reads in primary text.
              opt.disabled
                ? active
                  ? underline
                    ? 'text-primary/70'
                    : 'text-primary-foreground/70'
                  : 'text-muted-foreground opacity-50'
                : active
                  ? underline || pillNav
                    ? 'text-primary'
                    : tray
                      ? 'text-foreground'
                      : 'text-primary-foreground'
                  : pillNav
                    ? 'text-secondary-text hover:bg-neutral-20 hover:text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
            {opt.dot ? (
              <span
                className={cn(
                  'ml-1.5 inline-block size-1.5 shrink-0 rounded-full',
                  opt.dot === 'problem' ? 'bg-error' : 'bg-warning',
                )}
              >
                {opt.dotLabel ? <span className="sr-only">{opt.dotLabel}</span> : null}
              </span>
            ) : null}
            {opt.badge && (
              // Active-aware: on the active tab the sliding pill is already
              // `bg-primary`, so a solid-primary badge would vanish into it —
              // invert to a translucent light pill there; keep the solid pill
              // on inactive tabs where it contrasts against the container.
              // `badgeTone: 'problem'` swaps the primary tone for the same light error chip
              // every other status pill uses (G1 — colour only for real problems, never a plain
              // waiting/changed count).
              <span
                className={cn(
                  // A 20px circle for one digit, widening into a pill only for longer counts.
                  'ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] leading-none font-medium tabular-nums',
                  opt.badgeTone === 'problem'
                    ? 'bg-error-50 text-error'
                    : opt.badgeTone === 'attention'
                      ? 'bg-warning-50 text-warning'
                      : opt.badgeTone === 'muted'
                        ? active
                          ? 'bg-neutral-20 text-secondary-text'
                          : 'text-neutral-80'
                        : active && !underline && !pillNav && !tray
                          ? 'bg-primary-foreground/20 text-primary-foreground'
                          : 'bg-primary text-primary-foreground',
                )}
              >
                {opt.badge}
              </span>
            )}
          </Tag>
        );
        // Wrap in a tooltip when one is provided (e.g. explaining a disabled tab).
        // Tooltip.Root/Trigger(asChild) add no DOM wrapper, so the button stays a
        // direct flex child and its measured geometry for the pill is unaffected.
        if (!opt.tooltip) return button;
        return (
          <Tooltip key={opt.value} delayDuration={200}>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent>{opt.tooltip}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
